import { Box } from '@mui/material';
import useLocalization from '../../../../hooks/useLocalization';
import { useModal } from '../../../../hooks/useDialog';
import { AgentDetailsUser } from '../../../../@types';
import { useQueryClient, UseMutateFunction } from '@tanstack/react-query';
import z from 'zod';
import { QUERY_KEYS } from '../../../../constants';
import { TFunction } from 'i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import StyledForm from './StyledForm';
import StyledFormInput from './StyledFormInput';

type FieldName = 'firstName' | 'lastName' | 'password';
type UserDetailsFormValues = Partial<Pick<AgentDetailsUser, FieldName>>;

type UserDetailsFormModalProps = {
  title: string;
  fieldName: FieldName;
  userId: string;
  handleUpdate: UseMutateFunction<AgentDetailsUser, unknown, AgentDetailsUser, unknown>;
};

const getProfileSchema = (t: TFunction) => {
  return z.object({
    firstName: z.string().trim().min(1, t('register.validation.firstNameRequired')).optional(),
    lastName: z.string().trim().min(1, t('register.validation.lastNameRequired')).optional(),
    password: z.string().trim().min(8, t('register.validation.passwordTooShort')).optional(),
  });
};

const UserDetailsFormModal = ({ title, fieldName, userId, handleUpdate }: UserDetailsFormModalProps) => {
  const { t } = useLocalization();
  const { closeModal } = useModal();
  const queryClient = useQueryClient();

  const profileSchema = getProfileSchema(t);
  const defaultValues: UserDetailsFormValues = { [fieldName]: '' };
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<UserDetailsFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues,
  });

  const onSubmit = (data: UserDetailsFormValues) => {
    const payload: UserDetailsFormValues = { [fieldName]: data[fieldName] };

    handleUpdate(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.userDetails, userId] });
        closeModal();
      },
    });
  };

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)}>
      <StyledForm isSubmitting={isSubmitting}>
        <Controller
          name={fieldName}
          control={control}
          render={({ field }) => (
            <StyledFormInput
              {...field}
              autoFocus
              fullWidth
              value={field.value ?? ''}
              placeholder={title}
              error={!!errors[fieldName]}
              helperText={errors[fieldName]?.message}
            />
          )}
        />
      </StyledForm>
    </Box>
  );
};

export default UserDetailsFormModal;
